/**
 * Tailwind v4 uses @property CSS at-rules to initialize internal CSS variables.
 * @property declarations don't work inside Shadow DOM, so we must explicitly
 * set the default values here for shadows, borders, rings, gradients, etc. to render.
 *
 * Only properties with an explicit initial-value in Tailwind's @property rules
 * need initialization — properties without initial-value are "guaranteed invalid"
 * both with and without @property, so they behave the same in Shadow DOM.
 *
 * These MUST live in `@layer base` (not unlayered). Tailwind emits utilities into
 * `@layer utilities`, and unlayered declarations beat ANY layered declaration
 * regardless of specificity. If these defaults were unlayered, `*{--tw-gradient-from:#0000}`
 * and `*{--tw-shadow:0 0 #0000}` would override the `from-*` / `shadow-*` utilities,
 * making gradients render transparent and killing shadows entirely. Putting them in
 * `base` (an earlier layer than `utilities`) lets the utility values win.
 */
const twPropertyDefaults = `
  @layer base {
  *, ::before, ::after, ::backdrop {
    --tw-border-style: solid;
    --tw-outline-style: solid;
    --tw-shadow: 0 0 #0000;
    --tw-shadow-alpha: 100%;
    --tw-inset-shadow: 0 0 #0000;
    --tw-inset-shadow-alpha: 100%;
    --tw-ring-shadow: 0 0 #0000;
    --tw-inset-ring-shadow: 0 0 #0000;
    --tw-ring-offset-width: 0px;
    --tw-ring-offset-color: #fff;
    --tw-ring-offset-shadow: 0 0 #0000;
    --tw-drop-shadow-alpha: 100%;
    --tw-translate-x: 0;
    --tw-translate-y: 0;
    --tw-translate-z: 0;
    --tw-scale-x: 1;
    --tw-scale-y: 1;
    --tw-scale-z: 1;
    --tw-space-x-reverse: 0;
    --tw-space-y-reverse: 0;
    --tw-divide-x-reverse: 0;
    --tw-divide-y-reverse: 0;
    --tw-gradient-from: #0000;
    --tw-gradient-via: #0000;
    --tw-gradient-to: #0000;
    --tw-gradient-from-position: 0%;
    --tw-gradient-via-position: 50%;
    --tw-gradient-to-position: 100%;
  }
  }
`;

const buildFrameInnerStyles = (
  isComponents: boolean,
  skipBackground: boolean,
) => `
  :host > div {
    width: 100%;
    min-height: 100%;
    ${skipBackground ? '' : 'background-color: var(--background);'}
    color: var(--foreground);
  }

  /* Custom cursor for all elements inside the frame */
  :host > div,
  :host > div * {
    cursor: var(--cursor-default) !important;
  }

  ${!isComponents ? `* { font-family: inherit; }` : ''}

  ${isComponents ? twPropertyDefaults : ''}

  /* Disable native focus outline on actively editable text elements */
  [contenteditable]:focus {
    outline: none !important;
  }

  *[data-media-type='banani-button'] {
    cursor: pointer;
  }

  .h-screen {
    height: 100%;
  }

  .min-h-screen {
    min-height: 100%;
  }

  .max-h-screen {
    max-height: 100%;
  }
`;

const cachedFrameInnerStyles = {
  componentsWithBg: buildFrameInnerStyles(true, false),
  componentsNoBg: buildFrameInnerStyles(true, true),
  htmlCssWithBg: buildFrameInnerStyles(false, false),
  htmlCssNoBg: buildFrameInnerStyles(false, true),
};

export const getFrameInnerStyles = (
  isComponents: boolean,
  skipBackground = false,
): string => {
  if (isComponents) {
    return skipBackground
      ? cachedFrameInnerStyles.componentsNoBg
      : cachedFrameInnerStyles.componentsWithBg;
  }
  return skipBackground
    ? cachedFrameInnerStyles.htmlCssNoBg
    : cachedFrameInnerStyles.htmlCssWithBg;
};
